import { clstx } from '@payaca/utilities/cvaMerge';
import { ComponentPropsWithRef, ElementType } from 'react';

export type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4';
export type HeadingLevel = 1 | 2 | 3 | 4;

const LEVEL_UTILITY: Record<HeadingLevel, string> = {
  1: 'ambrosia-h1',
  2: 'ambrosia-h2',
  3: 'ambrosia-h3',
  4: 'ambrosia-h4',
};

/**
 * Native `<h1>`–`<h4>` attributes the component forwards (`aria-*`, `data-*`,
 * `id`, event handlers, etc.), minus the styling escape hatches `className` and
 * `style`. `ref` is included — React 19 passes it as a normal prop, so no
 * `forwardRef` is needed.
 */
type NativeHeadingProps = Omit<
  ComponentPropsWithRef<'h1'>,
  'className' | 'style'
>;

export type HeadingProps = NativeHeadingProps & {
  /** Semantic heading tag. Pick this for the document outline, not the look. */
  as: HeadingTag;
  /** Visual size (1 largest, 4 smallest). Defaults to the number in `as`. */
  level?: HeadingLevel;
  /** Keep to one line, cutting overflow with an ellipsis. */
  truncate?: boolean;
};

/**
 * A heading with locked-in typography. Renders one of `h1`–`h4` styled from the
 * shared design-system type scale — four levels of hierarchy.
 *
 * ## When to use
 *
 * - **Prefer the `H1`–`H4` components** for almost every heading. Each wraps
 *   `Heading` with the matching defaults (e.g. `H2` = `as="h2" level={2}`).
 * - Reach for `Heading` directly only when the semantic tag and the visual
 *   level must differ — e.g. an `<h1>` that should look like level 2:
 *   `<Heading as="h1" level={2}>`.
 *
 * ## When to choose another component
 *
 * - **Body copy, captions, labels** — use `Text`.
 *
 * ## Composition rules
 *
 * `as` sets the HTML tag (the outline); `level` sets the look. They are
 * independent. `truncate` keeps a long heading to one line; its container must
 * be able to shrink (e.g. `min-w-0` in a flex row). Never reach for raw `<h1>`–`<h4>` or text utility classes — this
 * component is the only sanctioned way to render a heading, and it exposes no
 * `className`, so its typography cannot be overridden.
 *
 * ✅ Correct:
 *
 * ```tsx
 * <H1>Page title</H1>
 * <Heading as="h1" level={2}>An h1 in the outline, styled as level 2</Heading>
 * ```
 *
 * ❌ Wrong:
 *
 * ```tsx
 * <h1 className="text-2xl font-extrabold">Page title</h1>
 * ```
 */
const Heading = ({ as, level, truncate, ...rest }: HeadingProps) => {
  const Tag = as as ElementType;
  const resolvedLevel = level ?? (Number(as.slice(1)) as HeadingLevel);

  return (
    <Tag
      {...rest}
      className={clstx(LEVEL_UTILITY[resolvedLevel], truncate && 'truncate')}
    />
  );
};

export default Heading;
